// Centralized date formatting utilities
// Format ISO timestamp (or Date) to "MM/DD/YYYY H:MM:SS" using local time.
// Returns '-' when input is falsy or invalid.
export function formatTimestampMDYHMS(value?: string | number | Date): string {
    if (value === undefined || value === null || value === '') return '-';
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
  
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
  
    // No leading zero for hour to match requested example "2:30:00"
    const h = String(date.getHours());
    const min = String(date.getMinutes()).padStart(2, '0');
    const ss = String(date.getSeconds()).padStart(2, '0');
  
    return `${mm}/${dd}/${yyyy} ${h}:${min}:${ss}`;
  }
  
  // passthrough for YYYY-MM-DD to MM/DD/YYYY without time
  export function formatDateYMDToMDY(value?: string): string {
    if (!value) return '-';
    // If already has slashes, return as-is
    if (value.includes('/')) return value;
    const parts = value.split('-');
    if (parts.length < 3) return value;
    const [y, m, d] = parts;
    return `${m.padStart(2, '0')}/${d.padStart(2, '0')}/${y}`;
  }
  

  export function formatTimestampMDY(value?: string | number | Date): string {
    if (value === undefined || value === null || value === '') return '-';
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
  
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
  
    return `${mm}-${dd}-${yyyy}`;
  }

  // Formats a date-only "YYYY-MM-DD" string to "MM-DD-YYYY" via string split —
  // avoids new Date() parsing so no UTC-to-local timezone day shift occurs.
  export function formatDateOnlyMDY(value?: string): string {
    if (!value) return '-';
    if (value.includes('/')) return value;
    // Ignore an optional time suffix without parsing through Date or applying timezone rules.
    const datePart = value.split(/[T ]/, 1)[0];
    const parts = datePart.split('-');
    if (parts.length < 3) return value;
    const [y, m, d] = parts;
    return `${m.padStart(2, '0')}-${d.padStart(2, '0')}-${y}`;
  }

  export function convertDateToYYYYMMDD(date: string): string {
      // Convert MM-DD-YYYY to YYYY-MM-DD
      const parts = date.split('-');
      if (parts.length === 3) {
        return `${parts[2]}-${parts[0]}-${parts[1]}`;
      }
      return date;
    };


  export function formatCostCenter(label: string): string {
    // Split by first hyphen and join with space
    const hyphenIndex = label.indexOf('-');
    if (hyphenIndex > 0) {
      const firstPart = label.substring(0, hyphenIndex).trim();
      const secondPart = label.substring(hyphenIndex + 1).trim();
      return `${firstPart} ${secondPart}`; 
    }
    return label;
  }

  // ─── Calendar arithmetic helpers (no new Date / no string parsing) ───────────

  /** Returns today's local year, 0-indexed month, and day. */
  export function getTodayParts(): { year: number; month: number; day: number } {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth(), day: now.getDate() };
  }

  /** Returns number of days in a given month (month is 0-indexed). */
  export function getDaysInMonth(year: number, month: number): number {
    const days = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    if (month === 1) {
      return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0) ? 29 : 28;
    }
    return days[month];
  }

  /**
   * Returns the day-of-week (0 = Sunday) for the 1st of the given month.
   * Uses Tomohiko Sakamoto's algorithm — pure arithmetic, no Date constructor.
   * month is 0-indexed.
   */
  export function getFirstDayOfMonth(year: number, month: number): number {
    const t = [0, 3, 2, 5, 0, 3, 5, 1, 4, 6, 2, 4];
    let y = year;
    if (month < 2) y -= 1;
    return (y + Math.floor(y / 4) - Math.floor(y / 100) + Math.floor(y / 400) + t[month] + 1) % 7;
  }

  const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  /** Returns the full English month name for a 0-indexed month. */
  export function getMonthName(month: number): string {
    return MONTH_NAMES[month] ?? '';
  }

  /**
   * Validates that (year, mm, dd) represents a real calendar date.
   * mm and dd are 1-indexed.
   */
  export function isValidCalendarDate(year: number, mm: number, dd: number): boolean {
    if (mm < 1 || mm > 12 || dd < 1) return false;
    return dd <= getDaysInMonth(year, mm - 1);
  }

  /**
   * Parses a date string in flexible formats and returns a normalized MM-DD-YYYY string.
   *
   * Accepted separators: `-` or `/`
   * Accepted patterns (M = 1–2 digit month, D = 1–2 digit day):
   *   M-D-YY, M/D/YY, MM-DD-YY, MM/DD/YY
   *   M-D-YYYY, M/D/YYYY, MM-DD-YYYY, MM/DD/YYYY
   *
   * 2-digit years are expanded to 20YY (e.g. 27 → 2027).
   * Returns `null` when the input is empty, malformed, not a real calendar date,
   * or the resolved year exceeds 2099.
   */
  export function parseFlexibleDateToMMDDYYYY(input: string): string | null {
    if (!input?.trim()) return null;
    const s = input.trim();

    const match = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4}|\d{2})$/.exec(s);
    if (!match) return null;

    const mm = Number.parseInt(match[1], 10);
    const dd = Number.parseInt(match[2], 10);
    const rawYear = Number.parseInt(match[3], 10);
    // Expand 2-digit year → 20YY
    const yyyy = match[3].length <= 2 ? 2000 + rawYear : rawYear;

    if (yyyy > 2099) return null;
    if (!isValidCalendarDate(yyyy, mm, dd)) return null;

    return `${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}-${yyyy}`;
  }

  /**
   * Compares two (year, month, day) tuples. month is 0-indexed.
   * Returns negative if a < b, 0 if equal, positive if a > b.
   */
  export function compareDateParts(
    y1: number, m1: number, d1: number,
    y2: number, m2: number, d2: number,
  ): number {
    if (y1 !== y2) return y1 - y2;
    if (m1 !== m2) return m1 - m2;
    return d1 - d2;
  }
